import React, { useState, useEffect, useCallback } from 'react';
import {
    Users, TrendingUp, Clock, Loader2, AlertTriangle,
    BarChart2, Target, Activity,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import MetricCard from './MetricCard';
import MetricDrawer from './MetricDrawer';
import { SectionHeader, Section, EmptyMetric } from './OverviewTabV2';

/**
 * RetentionTabV2 — "Are teachers coming back and forming habits?"
 */

const RetentionTabV2 = ({ filters = {} }) => {
    const [retention, setRetention] = useState(null);
    const [usageTime, setUsageTime] = useState(null);
    const [quality, setQuality] = useState(null);
    const [funnel, setFunnel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [drawerMetric, setDrawerMetric] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 7,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };

            const [retRes, utRes, sqRes, fnRes] = await Promise.allSettled([
                adminAnalytics.getRetention(params),
                adminAnalytics.getUsageTime(params),
                adminAnalytics.getSessionQuality({ days: filters.days || 7 }),
                adminAnalytics.getFunnel(params),
            ]);

            if (retRes.status === 'fulfilled') setRetention(retRes.value.data);
            if (utRes.status === 'fulfilled') setUsageTime(utRes.value.data);
            if (sqRes.status === 'fulfilled') setQuality(sqRes.value.data);
            if (fnRes.status === 'fulfilled') setFunnel(fnRes.value.data);
        } catch (err) {
            setError(err.message || 'Failed to load retention data.');
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading retention…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
            </div>
        );
    }

    const live = retention?.live_metrics || {};
    const cohorts = retention?.cohorts || [];
    const usage = usageTime || {};
    const highValueDist = (quality?.distribution || []).find(d => d.tier === 'high_value');

    return (
        <div>
            <SectionHeader
                title="Retention"
                subtitle="Are teachers coming back and forming habits?"
            />

            {/* ── Core Retention Metrics ──────────────────────────── */}
            <Section title="Retention Metrics" icon={TrendingUp} color="#10b981">
                <MetricCard
                    label="7-Day Return Rate"
                    value={live.week_over_week_return_rate_pct ?? '—'}
                    icon={TrendingUp} accent="#10b981" format="percent"
                />
                <MetricCard
                    label="30-Day Retention"
                    value={live.thirty_day_retention_rate_pct ?? '—'}
                    icon={Target} accent="#3b82f6" format="percent"
                />
                <MetricCard
                    label="Active Users"
                    value={live.active_users_current ?? '—'}
                    icon={Users} accent="#6366f1"
                    metricKey="active_users" onClick={setDrawerMetric}
                />
                {highValueDist ? (
                    <MetricCard
                        label="High-Value Session %"
                        value={highValueDist.pct}
                        icon={BarChart2} accent="#f59e0b" format="percent"
                    />
                ) : (
                    <EmptyMetric label="High-Value Session %" reason="no_data" />
                )}
            </Section>

            {/* ── Session Duration ────────────────────────────────── */}
            <Section title="Session Duration" icon={Clock} color="#8b5cf6">
                <MetricCard
                    label="Avg Session Duration"
                    value={usage.avg_session_duration_sec ? `${Math.round(usage.avg_session_duration_sec / 60)}m` : '—'}
                    icon={Clock} accent="#8b5cf6"
                    sub={usage.telemetry_status === 'insufficient' ? 'Partial telemetry' : null}
                />
                <MetricCard
                    label="Median Session Duration"
                    value={usage.median_session_duration_sec ? `${Math.round(usage.median_session_duration_sec / 60)}m` : '—'}
                    icon={Clock} accent="#6366f1"
                />
            </Section>

            {/* ── Activation Funnel ───────────────────────────────── */}
            <FunnelSection data={funnel} />

            {/* ── Cohort Data ────────────────────────────────────── */}
            {cohorts.length > 0 && (
                <div style={{ marginBottom: 28 }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        marginBottom: 12, paddingBottom: 8,
                        borderBottom: '2px solid #3b82f620',
                    }}>
                        <Activity size={15} style={{ color: '#3b82f6' }} />
                        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Retention Cohorts</h3>
                    </div>
                    <div style={{
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            display: 'grid', gridTemplateColumns: '1fr 100px 100px 100px',
                            padding: '10px 16px', background: '#f8fafc',
                            borderBottom: '1px solid #e5e7eb', gap: 8,
                        }}>
                            {['Cohort', 'Size', 'Retained', 'Rate'].map(h => (
                                <span key={h} style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>{h}</span>
                            ))}
                        </div>
                        {cohorts.slice(0, 10).map((c, i) => (
                            <div key={i} style={{
                                display: 'grid', gridTemplateColumns: '1fr 100px 100px 100px',
                                padding: '10px 16px', gap: 8,
                                borderBottom: i < Math.min(cohorts.length, 10) - 1 ? '1px solid #f1f5f9' : 'none',
                            }}>
                                <span style={{ fontSize: 12, color: '#374151' }}>{c.cohort_label || c.cohort_week}</span>
                                <span style={{ fontSize: 12, color: '#6b7280' }}>{c.cohort_size ?? '—'}</span>
                                <span style={{ fontSize: 12, color: '#6b7280' }}>{c.retained ?? '—'}</span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: c.retention_rate >= 50 ? '#10b981' : '#f59e0b' }}>
                                    {c.retention_rate != null ? `${c.retention_rate}%` : '—'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {drawerMetric && (
                <MetricDrawer metricKey={drawerMetric} filters={filters} onClose={() => setDrawerMetric(null)} />
            )}
        </div>
    );
};


// ─── Funnel Section ─────────────────────────────────────────────────────────

const FunnelSection = ({ data }) => {
    if (!data) return null;

    const steps = data.steps || [];
    if (steps.length === 0) return null;

    const maxCount = Math.max(...steps.map(s => s.distinct_users ?? s.count ?? 0), 1);

    return (
        <div style={{ marginBottom: 28 }}>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                marginBottom: 12, paddingBottom: 8,
                borderBottom: '2px solid #f59e0b20',
            }}>
                <Target size={15} style={{ color: '#f59e0b' }} />
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Activation Funnel</h3>
            </div>
            <div style={{
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 14, padding: '20px 24px',
            }}>
                {steps.map((step, idx) => {
                    const count = step.distinct_users ?? step.count ?? 0;
                    const barWidth = maxCount > 0 ? Math.max((count / maxCount) * 100, count > 0 ? 3 : 0) : 0;
                    return (
                        <div key={step.step || idx} style={{ marginBottom: idx < steps.length - 1 ? 12 : 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{
                                        width: 20, height: 20, borderRadius: '50%',
                                        background: '#f0f4f8', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', fontSize: 10, color: '#64748b', flexShrink: 0,
                                    }}>{idx + 1}</span>
                                    <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>
                                        {step.label || step.step}
                                    </span>
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 700, color: count > 0 ? '#111827' : '#d1d5db' }}>
                                    {count}
                                </span>
                            </div>
                            <div style={{ height: 5, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden', marginLeft: 28 }}>
                                <div style={{
                                    height: '100%', width: `${barWidth}%`,
                                    background: 'linear-gradient(90deg, #f59e0b, #f97316)',
                                    borderRadius: 3, transition: 'width 0.4s ease',
                                }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


export default RetentionTabV2;
